import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, ElementRef, Input, OnInit, signal, ViewChild } from '@angular/core';
import { ClientService } from 'src/app/services/client.service';
import { ProcoreService } from 'src/app/services/procore.service';
import { ContextService } from '../../../services/context.service';

interface Status {
	id: string;
	label: string;
}

interface ContractType {
	id: string;
	name: string;
	statuses: Status[];
}

interface ParsedApiError {
	httpStatus: number | null;       // e.g. 400
	statusText: string | null;       // e.g. "Bad Request"
	statusErrors: string[];          // e.g. ["cannot be edited if the Contract has any Change Orders"]
}

@Component({
	selector: 'app-project',
	templateUrl: './project.component.html',
	styleUrls: ['./project.component.css']
})
export class ProjectComponent implements OnInit {
	@ViewChild('assetManagerContainer') assetManagerContainer!: ElementRef;

	@ViewChild('generatePC') inputGeneratePC!: ElementRef<HTMLInputElement>;
	@ViewChild('generatePCCO') inputGeneratePCCO!: ElementRef<HTMLInputElement>;
	@ViewChild('generatePCCOB') inputGeneratePCCOB!: ElementRef<HTMLInputElement>;
	@ViewChild('generatePCPA') inputGeneratePCPA!: ElementRef<HTMLInputElement>;

	@ViewChild('generateCPO') inputGenerateCPO!: ElementRef<HTMLInputElement>;
	@ViewChild('generateCS') inputGenerateCS!: ElementRef<HTMLInputElement>;
	@ViewChild('generateCCO') inputGenerateCCO!: ElementRef<HTMLInputElement>;
	@ViewChild('generateCCOB') inputGenerateCCOB!: ElementRef<HTMLInputElement>;
	@ViewChild('generateCOR') inputGenerateCOR!: ElementRef<HTMLInputElement>;

	@Input() selectedProject;

	// Loading Vars
	loadingExportHistory = true;
	loadingGeneratePrimeContractAssets = false;
	loadingGenerateCommitmentAssets = false;
	loadingPrimeContractData = true;
	loadingCommitmentData = true;
	loadingProjectManagementData = false;

	// Client Vars
	client;

	// Project Vars
	selectedProjectFilterStatus;

	// Asset Manager Vars
	isAnyAssetCheckboxChecked = false;
	showAssetDeleteConfirm = false;
	showAssetStatusUpdateConfirm = false;
	showPrimeContractAssetCreator = false;
	showCommitmentAssetCreator = false;
	showUpdateStatusMenu = false;
	actionOccurringOnAssets = [];
	errorOccurredOnPrimeContracts = [];
	errorOccurredOnCommitments = [];

	selectedIds = signal<Set<string>>(new Set());
	availableStatuses = computed<Status[]>(() => {
		const selected = this.contractTypes.filter(c => this.selectedIds().has(c.id));
		if (selected.length === 0) return [];

		// For each selected type, build a set of its normalized status IDs
		const [first, ...rest] = selected;
		const restIdSets = rest.map(
			c => new Set(c.statuses.map(s => this.normalizeStatus(s.id)))
		);

		const seen = new Set<string>();
		return first.statuses.filter(status => {
			const key = this.normalizeStatus(status.id);
			if (seen.has(key)) return false;              // skip case-only duplicates
			seen.add(key);
			return restIdSets.every(ids => ids.has(key));
		});
	});

	contractTypes: ContractType[] = [
		{ id: 'primeContract', name: 'Prime Contract', statuses: [{ id: 'Draft', label: 'Draft' }, { id: 'Out For Bid', label: 'Out For Bid' }, { id: 'Out For Signature', label: 'Out For Signature' }, { id: 'Approved', label: 'Approved' }, { id: 'Complete', label: 'Complete' }, { id: 'Terminated', label: 'Terminated' }] },
		{ id: 'primeContractChangeOrder', name: 'Prime Contract Change Order', statuses: [{ id: 'draft', label: 'Draft' }, { id: 'pricing', label: 'Pricing' }, { id: 'not_pricing', label: 'Not Pricing' }, { id: 'pending', label: 'Pending' }, { id: 'revised', label: 'Revised' }, { id: 'proceeding', label: 'Proceeding' }, { id: 'not_proceeding', label: 'Not Proceeding' }, { id: 'pending_billable', label: 'Pending Billable' }, { id: 'no_charge', label: 'No Charge' }, { id: 'approved', label: 'Approved' }, { id: 'rejected', label: 'Rejected' }, { id: 'void', label: 'Void' }] },
		{ id: 'primeContractChangeOrderBatch', name: 'Prime Contract Change Order Batch', statuses: [{ id: 'draft', label: 'Draft' }, { id: 'pricing', label: 'Pricing' }, { id: 'not_pricing', label: 'Not Pricing' }, { id: 'pending', label: 'Pending' }, { id: 'revised', label: 'Revised' }, { id: 'proceeding', label: 'Proceeding' }, { id: 'not_proceeding', label: 'Not Proceeding' }, { id: 'pending_billable', label: 'Pending Billable' }, { id: 'no_charge', label: 'No Charge' }, { id: 'approved', label: 'Approved' }, { id: 'rejected', label: 'Rejected' }, { id: 'void', label: 'Void' }] },
		{ id: 'primeContractPaymentApplication', name: 'Prime Contract Payment Application', statuses: [{ id: 'draft', label: 'Draft' }, { id: 'under_review', label: 'Under Review' }, { id: 'revise_and_resubmit', label: 'Revise And Resubmit' }, { id: 'approved', label: 'Approved' }] },
		{ id: 'commitmentPurchaseOrder', name: 'Commitment Purchase Order', statuses: [{ id: 'Draft', label: 'Draft' }, { id: 'Processing', label: 'Processing' }, { id: 'Submitted', label: 'Submitted' }, { id: 'Partially Received', label: 'Partially Received' }, { id: 'Received', label: 'Received' }, { id: 'Approved', label: 'Approved' }, { id: 'Closed', label: 'Closed' }] },
		{ id: 'commitmentSubcontract', name: 'Commitment Subcontract', statuses: [{ id: 'Draft', label: 'Draft' }, { id: 'Out For Bid', label: 'Out For Bid' }, { id: 'Out For Signature', label: 'Out For Signature' }, { id: 'Approved', label: 'Approved' }, { id: 'Complete', label: 'Complete' }, { id: 'Terminated', label: 'Terminated' }] },
		{ id: 'commitmentChangeOrder', name: 'Commitment Change Order', statuses: [{ id: 'draft', label: 'Draft' }, { id: 'under_review', label: 'Under Review' }, { id: 'revise_and_resubmit', label: 'Revise And Resubmit' }, { id: 'approved', label: 'Approved' }] },
		{ id: 'commitmentChangeOrderBatch', name: 'Commitment Change Order Batch', statuses: [{ id: 'draft', label: 'Draft' }, { id: 'under_review', label: 'Under Review' }, { id: 'revise_and_resubmit', label: 'Revise And Resubmit' }, { id: 'approved', label: 'Approved' }] },
		{ id: 'commitmentRequisition', name: 'Commitment Requisition', statuses: [{ id: 'draft', label: 'Draft' }, { id: 'under_review', label: 'Under Review' }, { id: 'revise_and_resubmit', label: 'Revise And Resubmit' }, { id: 'approved', label: 'Approved' }, { id: 'approved_as_noted', label: 'Approved As Noted' }, { id: 'pending_owner_approval', label: 'Pending Owner Approval' }] },
	];

	// Document Status Views
	expandFinancialDocumentStatus = false;

	// Export Vars
	submitRetries = 0;
	exportHistories = [];

	constructor(
		private clientService: ClientService,
		public contextService: ContextService,
		private procoreService: ProcoreService
	) { }

	ngOnInit() {
		this.selectedProject.primeContracts = [];
		this.selectedProject.primeContractChangeOrders = [];
		this.selectedProject.primeContractChangeOrderBatches = [];
		this.selectedProject.primeContractPaymentApplications = [];
		this.selectedProject.commitmentPurchaseOrders = [];
		this.selectedProject.commitmentSubcontracts = [];
		this.selectedProject.commitmentChangeOrders = [];
		this.selectedProject.commitmentChangeOrderBatches = [];
		this.selectedProject.commitmentRequisitions = [];
		this.selectedProject.potentialChangeOrders = [];

		this.retrieveExportHistory(this.selectedProject);
	}

	retrieveExportHistory(project) {
		this.loadingExportHistory = true;

		this.clientService.getExportHistoriesByProjectId(this.contextService.procoreContextObject.context.company_id, project.id, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId)
			.subscribe({
				next: (response) => {
					this.exportHistories = response;

					this.loadingExportHistory = false;

					this.retrievePrimeContractData(this.selectedProject);
					this.retrieveCommitmentData(this.selectedProject);
				},
				error: () => {
					this.loadingExportHistory = false;

					this.retrievePrimeContractData(this.selectedProject);
					this.retrieveCommitmentData(this.selectedProject);
				}
			});
	}

	retrievePrimeContractData(project) {
		this.loadingPrimeContractData = true;

		this.errorOccurredOnPrimeContracts = [];

		this.procoreService.getPrimeContractData(this.contextService.procoreContextObject.context.company_id, project.id, this.contextService.procoreIsSandbox, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId)
			.subscribe({
				next: (response) => {
					if (response.potentialChangeOrders) {
						this.selectedProject.potentialChangeOrders = response.potentialChangeOrders;
					}
					if (response.primeContracts) {
						this.selectedProject.primeContracts = response.primeContracts;
					}
					if (response.primeContractChangeOrders) {
						this.selectedProject.primeContractChangeOrders = response.primeContractChangeOrders;
					}
					if (response.primeContractChangeOrderBatches) {
						this.selectedProject.primeContractChangeOrderBatches = response.primeContractChangeOrderBatches;
					}
					if (response.primeContractPaymentApplications) {
						this.selectedProject.primeContractPaymentApplications = response.primeContractPaymentApplications;
					}

					this.loadingPrimeContractData = false;

					this.calculatePrimeContractExportIcons();
				},
				error: () => {
					this.loadingPrimeContractData = false;
				}
			});
	}

	retrieveCommitmentData(project) {
		this.loadingCommitmentData = true;

		this.errorOccurredOnCommitments = [];

		this.procoreService.getCommitmentData(this.contextService.procoreContextObject.context.company_id, project.id, this.contextService.procoreIsSandbox, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId)
			.subscribe({
				next: (response) => {
					if (response.commitmentPurchaseOrders) {
						this.selectedProject.commitmentPurchaseOrders = response.commitmentPurchaseOrders;
					}
					if (response.commitmentSubcontracts) {
						this.selectedProject.commitmentSubcontracts = response.commitmentSubcontracts;
					}
					if (response.commitmentChangeOrders) {
						this.selectedProject.commitmentChangeOrders = response.commitmentChangeOrders;
					}
					if (response.commitmentRequisitions) {
						this.selectedProject.commitmentRequisitions = response.commitmentRequisitions;
					}

					this.loadingCommitmentData = false;

					this.calculateCommitmentExportIcons();
				},
				error: () => {
					this.loadingCommitmentData = false;
				}
			});
	}

	calculatePrimeContractExportIcons() {
		for (let primeContract of this.selectedProject.primeContracts) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == primeContract.id);
			if (exportHistory) {
				primeContract.exportedToDropbox = true;
				primeContract.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (primeContract.attachments) {
				for (let attachment of primeContract.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
		for (let primeContractChangeOrder of this.selectedProject.primeContractChangeOrders) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == primeContractChangeOrder.id);
			if (exportHistory) {
				primeContractChangeOrder.exportedToDropbox = true;
				primeContractChangeOrder.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (primeContractChangeOrder.attachments) {
				for (let attachment of primeContractChangeOrder.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
		for (let primeContractChangeOrderBatch of this.selectedProject.primeContractChangeOrderBatches) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == primeContractChangeOrderBatch.id);
			if (exportHistory) {
				primeContractChangeOrderBatch.exportedToDropbox = true;
				primeContractChangeOrderBatch.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (primeContractChangeOrderBatch.attachments) {
				for (let attachment of primeContractChangeOrderBatch.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
		for (let primeContractPaymentApplication of this.selectedProject.primeContractPaymentApplications) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == primeContractPaymentApplication.id);
			if (exportHistory) {
				primeContractPaymentApplication.exportedToDropbox = true;
				primeContractPaymentApplication.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (primeContractPaymentApplication.attachments) {
				for (let attachment of primeContractPaymentApplication.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
		for (let potentialChangeOrder of this.selectedProject.potentialChangeOrders) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == potentialChangeOrder.id);
			if (exportHistory) {
				potentialChangeOrder.exportedToDropbox = true;
				potentialChangeOrder.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (potentialChangeOrder.attachments) {
				for (let attachment of potentialChangeOrder.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
	}

	calculateCommitmentExportIcons() {
		for (let commitmentPurchaseOrder of this.selectedProject.commitmentPurchaseOrders) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == commitmentPurchaseOrder.id);
			if (exportHistory) {
				commitmentPurchaseOrder.exportedToDropbox = true;
				commitmentPurchaseOrder.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (commitmentPurchaseOrder.attachments) {
				for (let attachment of commitmentPurchaseOrder.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
		for (let commitmentSubcontract of this.selectedProject.commitmentSubcontracts) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == commitmentSubcontract.id);
			if (exportHistory) {
				commitmentSubcontract.exportedToDropbox = true;
				commitmentSubcontract.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (commitmentSubcontract.attachments) {
				for (let attachment of commitmentSubcontract.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
		for (let commitmentChangeOrder of this.selectedProject.commitmentChangeOrders) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == commitmentChangeOrder.id);
			if (exportHistory) {
				commitmentChangeOrder.exportedToDropbox = true;
				commitmentChangeOrder.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (commitmentChangeOrder.attachments) {
				for (let attachment of commitmentChangeOrder.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
		for (let commitmentChangeOrderBatch of this.selectedProject.commitmentChangeOrderBatches) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == commitmentChangeOrderBatch.id);
			if (exportHistory) {
				commitmentChangeOrderBatch.exportedToDropbox = true;
				commitmentChangeOrderBatch.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (commitmentChangeOrderBatch.attachments) {
				for (let attachment of commitmentChangeOrderBatch.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
		for (let commitmentRequisition of this.selectedProject.commitmentRequisitions) {
			let exportHistory = this.exportHistories.find(d => d.procoreItemId == commitmentRequisition.id);
			if (exportHistory) {
				commitmentRequisition.exportedToDropbox = true;
				commitmentRequisition.exportedToDropboxDate = exportHistory.exportDate;
			}

			if (commitmentRequisition.attachments) {
				for (let attachment of commitmentRequisition.attachments) {
					let exportHistory = this.exportHistories.find(d => d.procoreItemId == attachment.id);
					if (exportHistory) {
						attachment.exportedToDropbox = true;
						attachment.exportedToDropboxDate = exportHistory.exportDate;
					}
				}
			}
		}
	}

	//////////////////////

	selectPrimeContractCheckbox(event, item, contractTypeId) {
		item.checked = event.srcElement.checked;

		this.selectedIds.update(set => {
			const next = new Set(set);   // create a new Set so the signal sees a change
			event.srcElement.checked ? next.add(contractTypeId) : next.delete(contractTypeId);
			return next;
		});
	}

	// selectPrimeContractAttachmentCheckbox(event, item, contractTypeId) {
	// 	item.checked = event.srcElement.checked;

	// 	this.selectedIds.update(set => {
	// 		const next = new Set(set);   // create a new Set so the signal sees a change
	// 		event.srcElement.checked ? next.add(contractTypeId) : next.delete(contractTypeId);
	// 		return next;
	// 	});
	// }

	selectAllPrimeContractCheckboxes(event) {
		for (let primeContract of this.selectedProject.primeContracts) {
			primeContract.checked = event.srcElement.checked;

			if (primeContract.attachments) {
				for (let attachment of primeContract.attachments) {
					attachment.checked = event.srcElement.checked;
				}
			}
		}
		for (let primeContractChangeOrder of this.selectedProject.primeContractChangeOrders) {
			primeContractChangeOrder.checked = event.srcElement.checked;

			if (primeContractChangeOrder.attachments) {
				for (let attachment of primeContractChangeOrder.attachments) {
					attachment.checked = event.srcElement.checked;
				}
			}
		}
		for (let primeContractChangeOrderBatch of this.selectedProject.primeContractChangeOrderBatches) {
			primeContractChangeOrderBatch.checked = event.srcElement.checked;

			if (primeContractChangeOrderBatch.attachments) {
				for (let attachment of primeContractChangeOrderBatch.attachments) {
					attachment.checked = event.srcElement.checked;
				}
			}
		}
		for (let primeContractPaymentApplication of this.selectedProject.primeContractPaymentApplications) {
			primeContractPaymentApplication.checked = event.srcElement.checked;

			if (primeContractPaymentApplication.attachments) {
				for (let attachment of primeContractPaymentApplication.attachments) {
					attachment.checked = event.srcElement.checked;
				}
			}
		}
	}

	selectCommitmentCheckbox(event, item, contractTypeId) {
		item.checked = event.srcElement.checked;

		this.selectedIds.update(set => {
			const next = new Set(set);   // create a new Set so the signal sees a change
			event.srcElement.checked ? next.add(contractTypeId) : next.delete(contractTypeId);
			return next;
		});
	}

	selectAllCommitmentCheckboxes(event) {
		for (let commitmentPurchaseOrder of this.selectedProject.commitmentPurchaseOrders) {
			commitmentPurchaseOrder.checked = event.srcElement.checked;
		}
		for (let commitmentSubcontract of this.selectedProject.commitmentSubcontracts) {
			commitmentSubcontract.checked = event.srcElement.checked;
		}
		for (let commitmentChangeOrder of this.selectedProject.commitmentChangeOrders) {
			commitmentChangeOrder.checked = event.srcElement.checked;
		}
		for (let commitmentChangeOrderBatch of this.selectedProject.commitmentChangeOrderBatches) {
			commitmentChangeOrderBatch.checked = event.srcElement.checked;
		}
		for (let commitmentRequisition of this.selectedProject.commitmentRequisitions) {
			commitmentRequisition.checked = event.srcElement.checked;
		}
	}

	// This helps enable/disable the asset manager action menu depending on if a checkbox
	checkIfAnyCheckboxSelected() {
		// Find all checkbox inputs inside the container
		const checkboxes = this.assetManagerContainer.nativeElement.querySelectorAll('input[type="checkbox"]');

		// Check if any match the ':checked' pseudo-class
		this.isAnyAssetCheckboxChecked = Array.from(checkboxes).some((cb: any) => cb.checked);
	}

	deleteSelectedAssetsConfirm() {
		this.showAssetDeleteConfirm = true;
	}

	deleteSelectedAssets() {
		this.showAssetDeleteConfirm = false;

		for (let item of this.selectedProject.primeContractChangeOrders) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContractChangeOrder' });
			}
		}
		for (let item of this.selectedProject.primeContractChangeOrderBatches) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContractChangeOrderBatch' });
			}
		}
		for (let item of this.selectedProject.primeContractPaymentApplications) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContractPaymentApplication' });
			}
		}
		// Add these to the process queue last, otherwise they won't delete
		for (let item of this.selectedProject.primeContracts) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContract' });
			}
		}
		for (let item of this.selectedProject.commitmentChangeOrders) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentChangeOrder' });
			}
		}
		for (let item of this.selectedProject.commitmentChangeOrderBatches) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentChangeOrderBatch' });
			}
		}
		for (let item of this.selectedProject.commitmentRequisitions) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentRequisition' });
			}
		}
		// Add these to the process queue last, otherwise they won't delete
		for (let item of this.selectedProject.commitmentPurchaseOrders) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentPurchaseOrder' });
			}
		}
		for (let item of this.selectedProject.commitmentSubcontracts) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentSubcontract' });
			}
		}

		this.processDeleteQueue();
	}

	errorOccurredOnPrimeContractsContains(id) {
		return this.errorOccurredOnPrimeContracts.some(item => item.id === id);
	}

	errorOccurredOnPrimeContractsContainsValue(id) {
		let item = this.errorOccurredOnPrimeContracts.find(d => d.id === id);
		return item.message;
	}

	errorOccurredOnCommitmentsContains(id) {
		return this.errorOccurredOnCommitments.some(item => item.id === id);
	}

	errorOccurredOnCommitmentsContainsValue(id) {
		let item = this.errorOccurredOnCommitments.find(d => d.id === id);
		return item.message;
	}

	actionOccurringOnAssetsContains(id) {
		return this.actionOccurringOnAssets.some(item => item.id === id);
	}

	processDeleteQueue() {
		this.procoreService.deleteProjectAsset(this.contextService.procoreContextObject.context.company_id, this.contextService.procoreContextObject.context.project_id, this.actionOccurringOnAssets[0].id, this.actionOccurringOnAssets[0].type, this.contextService.procoreIsSandbox, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId)
			.subscribe({
				next: (response) => {
					if (response == 'Success') {
						console.log('Success');
						// Delete the item from the corresponding array
						if (this.actionOccurringOnAssets[0].type == 'primeContract') {
							let item = this.selectedProject.primeContracts.find(d => d.id === this.actionOccurringOnAssets[0].id);
							if (item) {
								this.selectedProject.primeContracts.splice(this.selectedProject.primeContracts.indexOf(item), 1);
							}
						} else if (this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder') {
							let item = this.selectedProject.primeContractChangeOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
							if (item) {
								this.selectedProject.primeContractChangeOrders.splice(this.selectedProject.primeContractChangeOrders.indexOf(item), 1);
							}
						} else if (this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch') {
							let item = this.selectedProject.primeContractChangeOrdersBatches.find(d => d.id === this.actionOccurringOnAssets[0].id);
							if (item) {
								this.selectedProject.primeContractChangeOrdersBatches.splice(this.selectedProject.primeContractChangeOrdersBatches.indexOf(item), 1);
							}
						} else if (this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
							let item = this.selectedProject.primeContractPaymentApplications.find(d => d.id === this.actionOccurringOnAssets[0].id);
							if (item) {
								this.selectedProject.primeContractPaymentApplications.splice(this.selectedProject.primeContractPaymentApplications.indexOf(item), 1);
							}
						} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder') {
							let item = this.selectedProject.commitmentPurchaseOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
							if (item) {
								this.selectedProject.commitmentPurchaseOrders.splice(this.selectedProject.commitmentPurchaseOrders.indexOf(item), 1);
							}
						} else if (this.actionOccurringOnAssets[0].type == 'commitmentSubcontract') {
							let item = this.selectedProject.commitmentSubcontracts.find(d => d.id === this.actionOccurringOnAssets[0].id);
							if (item) {
								this.selectedProject.commitmentSubcontracts.splice(this.selectedProject.commitmentSubcontracts.indexOf(item), 1);
							}
						} else if (this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder') {
							let item = this.selectedProject.commitmentChangeOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
							if (item) {
								this.selectedProject.commitmentChangeOrders.splice(this.selectedProject.commitmentChangeOrders.indexOf(item), 1);
							}
						} else if (this.actionOccurringOnAssets[0].type == 'commitmentChangeOrdersBatch') {
							let item = this.selectedProject.commitmentChangeOrdersBatches.find(d => d.id === this.actionOccurringOnAssets[0].id);
							if (item) {
								this.selectedProject.commitmentChangeOrdersBatches.splice(this.selectedProject.commitmentChangeOrdersBatches.indexOf(item), 1);
							}
						} else if (this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
							let item = this.selectedProject.commitmentRequisitions.find(d => d.id === this.actionOccurringOnAssets[0].id);
							if (item) {
								this.selectedProject.commitmentRequisitions.splice(this.selectedProject.commitmentRequisitions.indexOf(item), 1);
							}
						}
					} else {
						console.log('else');
						// TODO: Show an error
						if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
							this.errorOccurredOnPrimeContracts.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': response });
						} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
							this.errorOccurredOnCommitments.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': response });
						}
					}

					// Remove from the action queue
					this.actionOccurringOnAssets.splice(0, 1);

					// Keep processing deletions
					if (this.actionOccurringOnAssets.length > 0) {
						this.processDeleteQueue();
					}
				},
				error: (err) => {
					console.log('error');
					// TODO: Show an error
					if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
						this.errorOccurredOnPrimeContracts.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': err });
					} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
						this.errorOccurredOnCommitments.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': err });
					}

					// Remove from the action queue
					this.actionOccurringOnAssets.splice(0, 1);

					// Keep processing deletions
					if (this.actionOccurringOnAssets.length > 0) {
						this.processDeleteQueue();
					}
				}
			});
	}

	////

	getContractStatusLabel(contractTypeId: string, id: string): string | undefined {
		const contractType = this.contractTypes.find(ct => ct.id === contractTypeId);
		return contractType?.statuses.find(s => s.id === id)?.label;
	}

	getContractStatusId(contractTypeId: string, label: string): string | undefined {
		const contractType = this.contractTypes.find(ct => ct.id === contractTypeId);
		return contractType?.statuses.find(s => s.label === label)?.id;
	}

	updateStatusForSelectedAssets(statusLabel) {
		// NOTE: We pass in the label, as we need to lookup the ID based on type and label,
		//       as the id can be "Draft" or "draft" depending on the contract
		this.showUpdateStatusMenu = false;

		for (let item of this.selectedProject.primeContractChangeOrders) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContractChangeOrder' });
			}
		}
		for (let item of this.selectedProject.primeContractChangeOrderBatches) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContractChangeOrderBatch' });
			}
		}
		for (let item of this.selectedProject.primeContractPaymentApplications) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContractPaymentApplication' });
			}
		}
		for (let item of this.selectedProject.primeContracts) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContract' });
			}
		}
		for (let item of this.selectedProject.commitmentChangeOrders) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentChangeOrder' });
			}
		}
		for (let item of this.selectedProject.commitmentChangeOrderBatches) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentChangeOrderBatch' });
			}
		}
		for (let item of this.selectedProject.commitmentRequisitions) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentRequisition' });
			}
		}
		for (let item of this.selectedProject.commitmentPurchaseOrders) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentPurchaseOrder' });
			}
		}
		for (let item of this.selectedProject.commitmentSubcontracts) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentSubcontract' });
			}
		}

		this.processUpdateStatusQueue(statusLabel);
	}

	processUpdateStatusQueue(statusLabel) {
		// NOTE: We pass in the label, as we need to lookup the ID based on type and label,
		//       as the id can be "Draft" or "draft" depending on the contract

		// statusLabel = Draft
		// this.actionOccurringOnAssets[0].id = 652818
		// this.actionOccurringOnAssets[0].type = primeContract

		// statusLabel = Draft
		// this.actionOccurringOnAssets[0].id = 453817
		// this.actionOccurringOnAssets[0].type = primeContractChangeOrder

		let proceedWithUpdate = false;

		// Lookup the proper status for the contract type
		let status = this.getContractStatusId(this.actionOccurringOnAssets[0].type, statusLabel);

		// Ensure the contract doesn't already have the status we are updating to
		if (this.actionOccurringOnAssets[0].type == 'primeContract') {
			let item = this.selectedProject.primeContracts.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item.status !== status) {
				proceedWithUpdate = true;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder') {
			let item = this.selectedProject.primeContractChangeOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item.status !== status) {
				proceedWithUpdate = true;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'primeContractChangeOrderBatch') {
			let item = this.selectedProject.primeContractChangeOrderBatches.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item.status !== status) {
				proceedWithUpdate = true;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
			let item = this.selectedProject.primeContractPaymentApplications.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item.status !== status) {
				proceedWithUpdate = true;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder') {
			let item = this.selectedProject.commitmentPurchaseOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item.status !== status) {
				proceedWithUpdate = true;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentSubcontract') {
			let item = this.selectedProject.commitmentSubcontracts.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item.status !== status) {
				proceedWithUpdate = true;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder') {
			let item = this.selectedProject.commitmentChangeOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item.status !== status) {
				proceedWithUpdate = true;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentChangeOrdersBatch') {
			let item = this.selectedProject.commitmentChangeOrdersBatches.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item.status !== status) {
				proceedWithUpdate = true;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
			let item = this.selectedProject.commitmentRequisitions.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item.status !== status) {
				proceedWithUpdate = true;
			}
		}

		if (proceedWithUpdate) {
			// TODO: Perform the update
			this.procoreService.updateProjectAssetStatus(status, this.contextService.procoreContextObject.context.company_id, this.contextService.procoreContextObject.context.project_id, this.actionOccurringOnAssets[0].id, this.actionOccurringOnAssets[0].type, this.contextService.procoreIsSandbox, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId)
				.subscribe({
					next: (response) => {
						if (response) {
							// Update item in the corresponding array so the status updates on the UI
							if (this.actionOccurringOnAssets[0].type == 'primeContract') {
								let item = this.selectedProject.primeContracts.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									item.status = response.status;
									item.checked = false;
								}
							} else if (this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder') {
								let item = this.selectedProject.primeContractChangeOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									item.status = response.status;
									item.checked = false;
								}
							} else if (this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch') {
								let item = this.selectedProject.primeContractChangeOrdersBatches.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									item.status = response.status;
									item.checked = false;
								}
							} else if (this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
								let item = this.selectedProject.primeContractPaymentApplications.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									item.status = response.status;
									item.checked = false;
								}
							} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder') {
								let item = this.selectedProject.commitmentPurchaseOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									item.status = response.status;
									item.checked = false;
								}
							} else if (this.actionOccurringOnAssets[0].type == 'commitmentSubcontract') {
								let item = this.selectedProject.commitmentSubcontracts.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									item.status = response.status;
									item.checked = false;
								}
							} else if (this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder') {
								let item = this.selectedProject.commitmentChangeOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									item.status = response.status;
									item.checked = false;
								}
							} else if (this.actionOccurringOnAssets[0].type == 'commitmentChangeOrdersBatch') {
								let item = this.selectedProject.commitmentChangeOrdersBatches.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									item.status = response.status;
									item.checked = false;
								}
							} else if (this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
								let item = this.selectedProject.commitmentRequisitions.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									item.status = response.status;
									item.checked = false;
								}
							}
						} else {
							if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
								this.errorOccurredOnPrimeContracts.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'An error occurred' });
							} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
								this.errorOccurredOnCommitments.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'An error occurred' });
							}
						}

						// Uncheck the checkbox
						this.uncheckItem(this.actionOccurringOnAssets[0].id);

						// Remove from the action queue
						this.actionOccurringOnAssets.splice(0, 1);

						// Keep processing deletions
						if (this.actionOccurringOnAssets.length > 0) {
							this.processUpdateStatusQueue(statusLabel);
						}
					},
					error: (err: HttpErrorResponse) => {
						console.log('error');
						// Show an error
						let error = this.extractStatusFromError(err.error.message);
						if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
							this.errorOccurredOnPrimeContracts.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': error.statusErrors[0] });
						} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
							this.errorOccurredOnCommitments.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': error.statusErrors[0] });
						}


						// Uncheck the checkbox
						this.uncheckItem(this.actionOccurringOnAssets[0].id);

						// Remove from the action queue
						this.actionOccurringOnAssets.splice(0, 1);

						// Keep processing deletions
						if (this.actionOccurringOnAssets.length > 0) {
							this.processUpdateStatusQueue(statusLabel);
						}
					}
				});


		} else {
			// Uncheck the checkbox
			this.uncheckItem(this.actionOccurringOnAssets[0].id);

			// Remove from the action queue
			this.actionOccurringOnAssets.splice(0, 1);

			// Keep processing updates
			if (this.actionOccurringOnAssets.length > 0) {
				this.processUpdateStatusQueue(statusLabel);
			}
		}
	}

	uncheckItem(itemId) {
		if (this.actionOccurringOnAssets[0].type == 'primeContract') {
			let item = this.selectedProject.primeContracts.find(d => d.id === itemId);
			if (item) {
				item.checked = false;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder') {
			let item = this.selectedProject.primeContractChangeOrders.find(d => d.id === itemId);
			if (item) {
				item.checked = false;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch') {
			let item = this.selectedProject.primeContractChangeOrdersBatches.find(d => d.id === itemId);
			if (item) {
				item.checked = false;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
			let item = this.selectedProject.primeContractPaymentApplications.find(d => d.id === itemId);
			if (item) {
				item.checked = false;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder') {
			let item = this.selectedProject.commitmentPurchaseOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item) {
				item.checked = false;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentSubcontract') {
			let item = this.selectedProject.commitmentSubcontracts.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item) {
				item.checked = false;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder') {
			let item = this.selectedProject.commitmentChangeOrders.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item) {
				item.checked = false;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentChangeOrdersBatch') {
			let item = this.selectedProject.commitmentChangeOrdersBatches.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item) {
				item.checked = false;
			}
		} else if (this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
			let item = this.selectedProject.commitmentRequisitions.find(d => d.id === this.actionOccurringOnAssets[0].id);
			if (item) {
				item.checked = false;
			}
		}
	}

	extractStatusFromError(raw: string): ParsedApiError {
		const result: ParsedApiError = { httpStatus: null, statusText: null, statusErrors: [] };
		if (!raw) return result;

		// HTTP code and text, e.g. "400 Bad Request:"
		const header = raw.match(/^\s*(\d{3})\s+([^:]+):/);
		if (header) {
			result.httpStatus = Number(header[1]);
			result.statusText = header[2].trim();
		}

		// JSON body: everything from the first "{" to the last "}"
		const start = raw.indexOf('{');
		const end = raw.lastIndexOf('}');
		if (start !== -1 && end > start) {
			try {
				const body = JSON.parse(raw.substring(start, end + 1));
				const status = body?.errors?.status;
				if (Array.isArray(status)) {
					result.statusErrors = status.map(String);
				} else if (typeof status === 'string') {
					result.statusErrors = [status];
				}
			} catch {
				// Body wasn't valid JSON; leave statusErrors empty
			}
		}

		return result;
	}

	////

	generatePrimeContractData(project) {
		this.loadingGeneratePrimeContractAssets = true;

		let pc = Number(this.inputGeneratePC.nativeElement.value);
		let pcco = Number(this.inputGeneratePCCO.nativeElement.value);
		let pccob = Number(this.inputGeneratePCCOB.nativeElement.value);
		let pcpa = Number(this.inputGeneratePCPA.nativeElement.value);

		this.procoreService.generatePrimeContractAssets(pc, pcco, pccob, pcpa, this.contextService.procoreContextObject.context.company_id, project.id, true, this.contextService.procoreIsSandbox, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId)
			.subscribe({
				next: (response) => {
					this.loadingGeneratePrimeContractAssets = false;
					this.showPrimeContractAssetCreator = false;

					this.retrievePrimeContractData(project);
				},
				error: () => {
					this.loadingGeneratePrimeContractAssets = false;
					this.showPrimeContractAssetCreator = false;

					this.retrievePrimeContractData(project);
				}
			});
	}

	generateCommitmentData(project) {
		this.loadingGenerateCommitmentAssets = true;

		let cpo = Number(this.inputGenerateCPO.nativeElement.value);
		let cs = Number(this.inputGenerateCS.nativeElement.value);
		let cco = Number(this.inputGenerateCCO.nativeElement.value);
		let ccob = Number(this.inputGenerateCCOB.nativeElement.value);
		let cor = Number(this.inputGenerateCOR.nativeElement.value);

		this.procoreService.generateCommitmentAssets(cpo, cs, cco, ccob, cor, this.contextService.procoreContextObject.context.company_id, project.id, true, this.contextService.procoreIsSandbox, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId)
			.subscribe({
				next: (response) => {
					this.loadingGenerateCommitmentAssets = false;
					this.showCommitmentAssetCreator = false;

					this.retrieveCommitmentData(project);
				},
				error: () => {
					this.loadingGenerateCommitmentAssets = false;
					this.showCommitmentAssetCreator = false;

					this.retrieveCommitmentData(project);
				}
			});
	}

	showStatusMenu() {
		if (this.showUpdateStatusMenu) {
			// Menu was open, hiding it
			this.showUpdateStatusMenu = false;
		} else {
			// Menu was closed, calculate statuses and open it
			this.showUpdateStatusMenu = true;
		}
	}

	normalizeStatus(id: string): string {
		return id.trim().toLowerCase();
	}

	getChangeOrderTag(itemId, type) {
		let item = this.selectedProject.potentialChangeOrders.find(d => d.id === itemId);
		if (item) {
			if (type == 'primeContractChangeOrder') {
				if (item.change_order_tiers == 1) {
					return 'CO';
				} if (item.change_order_tiers == 2) {
					return 'PCO';
				} else {
					// ?
				}
			} else if (type == 'primeContractChangeOrderBatch') {
				if (item.change_order_tiers == 1) {
					return ''; // NOT POSSIBLE?
				} if (item.change_order_tiers == 2) {
					return 'CO';
				} else {
					// ?
				}
			} else if (type == 'commitmentChangeOrder') {
				if (item.change_order_tiers == 1) {
					return 'CO';
				} if (item.change_order_tiers == 2) {
					return 'PCO';
				} else {
					// ?
				}
			} else if (type == 'commitmentChangeOrderBatch') {
				if (item.change_order_tiers == 1) {
					return '';  // NOT POSSIBLE?
				} if (item.change_order_tiers == 2) {
					return 'CO';
				} else {
					// ?
				}
			}
		} else {
			return 'CO';
		}

		return '';
	}

	getChangeOrderTitle(itemId, type) {
		let item = this.selectedProject.potentialChangeOrders.find(d => d.id === itemId);
		if (item) {
			if (type == 'primeContractChangeOrder') {
				if (item.change_order_tiers == 1) {
					return 'Change Order';
				} if (item.change_order_tiers == 2) {
					return 'Potential Change Order';
				} else {
					// ?
				}
			} else if (type == 'primeContractChangeOrderBatch') {
				if (item.change_order_tiers == 1) {
					return ''; // NOT POSSIBLE?
				} if (item.change_order_tiers == 2) {
					return 'Change OrderCO';
				} else {
					// ?
				}
			} else if (type == 'commitmentChangeOrder') {
				if (item.change_order_tiers == 1) {
					return 'Change Order';
				} if (item.change_order_tiers == 2) {
					return 'Potential Change Order';
				} else {
					// ?
				}
			} else if (type == 'commitmentChangeOrderBatch') {
				if (item.change_order_tiers == 1) {
					return '';  // NOT POSSIBLE?
				} if (item.change_order_tiers == 2) {
					return 'Change Order';
				} else {
					// ?
				}
			}
		} else {
			return 'Change Order';
		}

		return '';
	}

	////

	exportSelectedAssets() {
		for (let item of this.selectedProject.primeContractChangeOrders) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContractChangeOrder' });
			}

			if (item.attachments) {
				for (let attachment of item.attachments) {
					if (attachment.checked) {
						this.actionOccurringOnAssets.push({ 'id': attachment.id, 'url': attachment.url, 'type': 'primeContractChangeOrderAttachment' });
					}
				}
			}
		}
		for (let item of this.selectedProject.primeContractChangeOrderBatches) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContractChangeOrderBatch' });
			}

			if (item.attachments) {
				for (let attachment of item.attachments) {
					if (attachment.checked) {
						this.actionOccurringOnAssets.push({ 'id': attachment.id, 'url': attachment.url, 'type': 'primeContractChangeOrderBatchAttachment' });
					}
				}
			}
		}
		for (let item of this.selectedProject.primeContractPaymentApplications) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContractPaymentApplication' });
			}

			if (item.attachments) {
				for (let attachment of item.attachments) {
					if (attachment.checked) {
						this.actionOccurringOnAssets.push({ 'id': attachment.id, 'url': attachment.url, 'type': 'primeContractPaymentApplicationAttachment' });
					}
				}
			}
		}
		// Add these to the process queue last, otherwise they won't delete
		for (let item of this.selectedProject.primeContracts) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'primeContract' });
			}

			if (item.attachments) {
				for (let attachment of item.attachments) {
					if (attachment.checked) {
						this.actionOccurringOnAssets.push({ 'id': attachment.id, 'url': attachment.url, 'type': 'primeContractAttachment' });
					}
				}
			}
		}
		for (let item of this.selectedProject.commitmentChangeOrders) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentChangeOrder' });
			}

			if (item.attachments) {
				for (let attachment of item.attachments) {
					if (attachment.checked) {
						this.actionOccurringOnAssets.push({ 'id': attachment.id, 'url': attachment.url, 'type': 'commitmentChangeOrderAttachment' });
					}
				}
			}
		}
		for (let item of this.selectedProject.commitmentChangeOrderBatches) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentChangeOrderBatch' });
			}

			if (item.attachments) {
				for (let attachment of item.attachments) {
					if (attachment.checked) {
						this.actionOccurringOnAssets.push({ 'id': attachment.id, 'url': attachment.url, 'type': 'commitmentChangeOrderBatchAttachment' });
					}
				}
			}
		}
		for (let item of this.selectedProject.commitmentRequisitions) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentRequisition' });
			}

			if (item.attachments) {
				for (let attachment of item.attachments) {
					if (attachment.checked) {
						this.actionOccurringOnAssets.push({ 'id': attachment.id, 'url': attachment.url, 'type': 'commitmentRequisitionAttachment' });
					}
				}
			}
		}
		// Add these to the process queue last, otherwise they won't delete
		for (let item of this.selectedProject.commitmentPurchaseOrders) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentPurchaseOrder' });
			}

			if (item.attachments) {
				for (let attachment of item.attachments) {
					if (attachment.checked) {
						this.actionOccurringOnAssets.push({ 'id': attachment.id, 'url': attachment.url, 'type': 'commitmentPurchaseOrderAttachment' });
					}
				}
			}
		}
		for (let item of this.selectedProject.commitmentSubcontracts) {
			if (item.checked) {
				this.actionOccurringOnAssets.push({ 'id': item.id, 'type': 'commitmentSubcontract' });
			}

			if (item.attachments) {
				for (let attachment of item.attachments) {
					if (attachment.checked) {
						this.actionOccurringOnAssets.push({ 'id': attachment.id, 'url': attachment.url, 'type': 'commitmentSubcontractAttachment' });
					}
				}
			}
		}

		this.processExportQueue();
	}

	processExportQueue() {
		if (this.actionOccurringOnAssets[0].type.includes('Attachment')) {
			this.processAttachmentExportFinish(this.actionOccurringOnAssets[0].url);
		} else {
			this.procoreService.exportAssetStart(this.contextService.procoreContextObject.context.company_id, this.contextService.procoreContextObject.context.project_id, this.actionOccurringOnAssets[0].id, this.actionOccurringOnAssets[0].type, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId, this.contextService.procoreIsSandbox)
				.subscribe({
					next: (response) => {
						this.processAssetExportFinish(response);
					},
					error: (err) => {
						console.log('error');
						// TODO: Show an error
						if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
							this.errorOccurredOnPrimeContracts.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': err });
						} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
							this.errorOccurredOnCommitments.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': err });
						}

						// Remove from the action queue
						this.actionOccurringOnAssets.splice(0, 1);

						// Keep processing deletions
						if (this.actionOccurringOnAssets.length > 0) {
							this.processExportQueue();
						}
					}
				});
		}
	}

	processAttachmentExportFinish(url) {
		this.procoreService.exportAssetAttachmentToDropbox(url, this.contextService.procoreContextObject.context.company_id, this.contextService.procoreContextObject.context.project_id, this.actionOccurringOnAssets[0].id, this.actionOccurringOnAssets[0].type, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId, this.contextService.procoreIsSandbox)
			.subscribe({
				next: (response) => {
					// Add export history
					let exportHistory = {
						'procoreCompanyId': this.contextService.procoreContextObject.context.company_id,
						'procoreProjectId': this.contextService.procoreContextObject.context.project_id,
						'procoreItemId': this.actionOccurringOnAssets[0].id,
						'procoreItemType': this.actionOccurringOnAssets[0].type,
						'exportDate': new Date(),
						'exportTo': 'dropbox'
					};
					console.log(exportHistory);
					this.exportHistories.push(exportHistory);
					this.calculatePrimeContractExportIcons();
					this.calculateCommitmentExportIcons();

					// Hide error
					// if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
					// 	let item = this.errorOccurredOnPrimeContracts.find(d => d.id === this.actionOccurringOnAssets[0].id);
					// 	if (item) {
					// 		this.errorOccurredOnPrimeContracts.splice(this.errorOccurredOnPrimeContracts.indexOf(item), 1);
					// 	}
					// } else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
					// 	let item = this.errorOccurredOnCommitments.find(d => d.id === this.actionOccurringOnAssets[0].id);
					// 	if (item) {
					// 		this.errorOccurredOnCommitments.splice(this.errorOccurredOnCommitments.indexOf(item), 1);
					// 	}
					// }

					// Remove from the action queue
					this.actionOccurringOnAssets.splice(0, 1);

					// Keep processing deletions
					if (this.actionOccurringOnAssets.length > 0) {
						this.processExportQueue();
					}
				},
				error: (err) => {
					console.log("processAttachmentExportFinish err");
					console.log(err);

					// Show an error
					// if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
					// 	this.errorOccurredOnPrimeContracts.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'There was an issue generating the file' });
					// } else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
					// 	this.errorOccurredOnCommitments.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'There was an issue generating the file' });
					// }

					// Remove from the action queue
					this.actionOccurringOnAssets.splice(0, 1);

					// Keep processing deletions
					if (this.actionOccurringOnAssets.length > 0) {
						this.processExportQueue();
					}
				}
			});
	}

	processAssetExportFinish(url) {
		this.submitRetries = this.submitRetries + 1;

		if (this.submitRetries < 10) {
			this.procoreService.exportAssetToDropbox(url, this.contextService.procoreContextObject.context.company_id, this.contextService.procoreContextObject.context.project_id, this.actionOccurringOnAssets[0].id, this.actionOccurringOnAssets[0].type, this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.procoreUserId, this.contextService.procoreIsSandbox)
				.subscribe({
					next: (response) => {
						console.log("processExportFinish response");
						console.log(response);

						if (response.status === 204) {
							console.log('error 204');
							this.submitRetries = 0;

							// Show an error
							if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
								this.errorOccurredOnPrimeContracts.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'There was an issue generating the file' });
							} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
								this.errorOccurredOnCommitments.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'There was an issue generating the file' });
							}

							// Remove from the action queue
							this.actionOccurringOnAssets.splice(0, 1);

							// Keep processing deletions
							if (this.actionOccurringOnAssets.length > 0) {
								this.processExportQueue();
							}
						} else if (response.status === 202) {
							console.log('waiting 202');
							let _this = this;
							setTimeout(function () {
								_this.processAssetExportFinish(url);
							}, 5000);
						} else {
							console.log('success 200');
							this.submitRetries = 0;

							// Add export history
							let exportHistory = {
								'procoreCompanyId': this.contextService.procoreContextObject.context.company_id,
								'procoreProjectId': this.contextService.procoreContextObject.context.project_id,
								'procoreItemId': this.actionOccurringOnAssets[0].id,
								'procoreItemType': this.actionOccurringOnAssets[0].type,
								'exportDate': new Date(),
								'exportTo': 'dropbox'
							};
							console.log(exportHistory);
							this.exportHistories.push(exportHistory);
							this.calculatePrimeContractExportIcons();
							this.calculateCommitmentExportIcons();

							// Hide error
							if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
								let item = this.errorOccurredOnPrimeContracts.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									this.errorOccurredOnPrimeContracts.splice(this.errorOccurredOnPrimeContracts.indexOf(item), 1);
								}
							} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
								let item = this.errorOccurredOnCommitments.find(d => d.id === this.actionOccurringOnAssets[0].id);
								if (item) {
									this.errorOccurredOnCommitments.splice(this.errorOccurredOnCommitments.indexOf(item), 1);
								}
							}

							// Remove from the action queue
							this.actionOccurringOnAssets.splice(0, 1);

							// Keep processing deletions
							if (this.actionOccurringOnAssets.length > 0) {
								this.processExportQueue();
							}
						}
					},
					error: (err) => {
						console.log("submitGeneratedPDFFinish err");
						console.log(err);
						this.submitRetries = 0;

						// Show an error
						if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
							this.errorOccurredOnPrimeContracts.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'There was an issue generating the file' });
						} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
							this.errorOccurredOnCommitments.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'There was an issue generating the file' });
						}

						// Remove from the action queue
						this.actionOccurringOnAssets.splice(0, 1);

						// Keep processing deletions
						if (this.actionOccurringOnAssets.length > 0) {
							this.processExportQueue();
						}
					}
				});
		} else {
			this.submitRetries = 0;

			// Show an error
			if (this.actionOccurringOnAssets[0].type == 'primeContract' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrder' || this.actionOccurringOnAssets[0].type == 'primeContractChangeOrdersBatch' || this.actionOccurringOnAssets[0].type == 'primeContractPaymentApplication') {
				this.errorOccurredOnPrimeContracts.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'There was an issue generating the file' });
			} else if (this.actionOccurringOnAssets[0].type == 'commitmentPurchaseOrder' || this.actionOccurringOnAssets[0].type == 'commitmentSubcontract' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrder' || this.actionOccurringOnAssets[0].type == 'commitmentChangeOrderBatch' || this.actionOccurringOnAssets[0].type == 'commitmentRequisition') {
				this.errorOccurredOnCommitments.push({ 'id': this.actionOccurringOnAssets[0].id, 'type': this.actionOccurringOnAssets[0].type, 'message': 'There was an issue generating the file' });
			}

			// Remove from the action queue
			this.actionOccurringOnAssets.splice(0, 1);

			// Keep processing deletions
			if (this.actionOccurringOnAssets.length > 0) {
				this.processExportQueue();
			}
		}
	}
}
